import Layout from "modules/layout/dashboard"

import React from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import Loader from "components/loader"
import { Button, Empty, Flex, Input, Select, Space, Tag } from "antd"
import { useTranslation } from "react-i18next"
import { ReactComponent as AddIcon } from "assets/icons/add.svg"
import { useActivityExpensesTracking } from "./queries"
import OverviewCardWithDate from "components/overview-card-with-date"
import type { IActivityExpense } from "./types"
import { ReactComponent as EditIcon } from "assets/icons/edit.svg"
import { ReactComponent as DuplicateIcon } from "assets/icons/duplicate.svg"
import { formatCurrency } from "utils/format-currency"
import PageHeader from "modules/layout/dashboard/main/page-header"
import Title from "antd/es/typography/Title"
import useWindowDimensions from "utils/use-window-dimension"
import { useExtSessions } from "pages/assignations/preassignations/queries"

const statusTagColors = (status: IActivityExpense["status"]) => {
  switch (status) {
    case "approved":
      return "var(--color-success)"
    case "refused":
      return "var(--color-error)"
    case "pending":
      return "var(--color-accent2)"
    default:
      return "var(--color-accent2)"
  }
}

const { Main, Header } = Layout

const SEARCH_PARAM = "search"
const SESSION_PARAM = "session"

const ActivityExpensesTrackingList: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { width } = useWindowDimensions()
  const [searchParams, setSearchParams] = useSearchParams()

  const sessions = useExtSessions(undefined, {
    onSuccess(data) {
      if (!searchParams.get("session")) {
        searchParams.set(
          "session",
          data.find((session) => session.status === "progress")?.id ||
            data.find((session) => session.status === "published")?.id ||
            ""
        )
        setSearchParams(searchParams)
      }
    },
  })

  const { data, isLoading } = useActivityExpensesTracking(
    {
      session_id: searchParams.get(SESSION_PARAM)!,
    },
    {
      enabled: !!searchParams.get(SESSION_PARAM),
    }
  )

  return (
    <Layout>
      <Header />
      <Main>
        <Flex vertical style={{ margin: "16pt", flexGrow: 1 }} gap="middle">
          <PageHeader
            title={<Title level={4}>{t("finance.expense_entries")}</Title>}
            hasBackButton={false}
            rightContent={
              <Button
                style={{
                  display: "flex",
                  alignItems: "center",
                }}
                disabled={!searchParams.get(SESSION_PARAM)}
                size={width! > 520 ? "large" : "middle"}
                type="primary"
                onClick={() =>
                  navigate(
                    `create?${SESSION_PARAM}=${searchParams.get(SESSION_PARAM)}`
                  )
                }
              >
                <AddIcon style={width! > 520 ? { marginRight: 10 } : {}} />
                {width! > 520 &&
                  t("global.new", {
                    entity: t("finance.expense"),
                    context: "male",
                    count: 1,
                  })}
              </Button>
            }
          />
          <Input
            size="large"
            placeholder={t("global.search")}
            value={searchParams.get(SEARCH_PARAM) ?? ""}
            onChange={(e) => {
              e.target.value
                ? searchParams.set(SEARCH_PARAM, e.target.value)
                : searchParams.delete(SEARCH_PARAM)
              setSearchParams(searchParams, { replace: true })
            }}
          />
          <Select
            value={searchParams.get(SESSION_PARAM)}
            style={{ width: "100%" }}
            placeholder={t("global.select", {
              entity: t("labels.session"),
              context: "female",
            })}
            loading={sessions.isLoading}
            onChange={(value) => {
              searchParams.set(SESSION_PARAM, value)
              setSearchParams(searchParams, { replace: true })
            }}
            options={sessions.data?.map((session) => ({
              value: session.id,
              label: session.name,
            }))}
          />
          {isLoading ? (
            <Loader />
          ) : data?.length === 0 ? (
            <Empty
              style={{
                marginTop: "var(--size-10)",
              }}
              description={t("global.no_x_found", {
                entity: t("finance.expense"),
                context: "male",
                count: 1,
              })}
            />
          ) : (
            <Space direction="vertical">
              {data
                ?.filter((expense) =>
                  expense.activity.name
                    .toLowerCase()
                    .includes(
                      searchParams.get(SEARCH_PARAM)?.toLowerCase() ?? ""
                    )
                )
                .map((expense) => (
                  <OverviewCardWithDate
                    key={expense.id}
                    date={expense.date}
                    actions={[
                      <Tag
                        style={{
                          marginRight: 0,
                        }}
                        color={statusTagColors(expense.status)}
                      >
                        {expense.display_status}
                      </Tag>,
                      <Button
                        style={{
                          width: "100%",
                        }}
                        icon={<EditIcon height={20} />}
                        size="small"
                        type="text"
                        disabled={["approved", "refused"].includes(
                          expense.status
                        )}
                        onClick={() =>
                          navigate(
                            `${
                              expense.id
                            }/edit?${SESSION_PARAM}=${searchParams.get(
                              SESSION_PARAM
                            )}`
                          )
                        }
                      />,
                      <Button
                        style={{
                          width: "100%",
                        }}
                        icon={<DuplicateIcon height={20} />}
                        size="small"
                        type="text"
                        disabled={["approved", "pending"].includes(
                          expense.status
                        )}
                        onClick={() =>
                          navigate(
                            `${
                              expense.id
                            }/duplicate?${SESSION_PARAM}=${searchParams.get(
                              SESSION_PARAM
                            )}`
                          )
                        }
                      />,
                    ]}
                    Content={
                      <>
                        <p>{expense.activity.name}</p>
                        <p>{expense.post.name}</p>
                        <p>{formatCurrency(expense.amount)}</p>
                      </>
                    }
                  />
                ))}
            </Space>
          )}
        </Flex>
      </Main>
    </Layout>
  )
}

export default ActivityExpensesTrackingList
